import { useCallback, useMemo } from 'react';

import { useActorRef } from '@xstate/react';
import { format } from 'date-fns';
import { fromPromise } from 'xstate';

import { useNoticeBannersStackActions } from '@sg/garnish';

import { useAppsFlyer } from '@order/integrations-appsflyer';
import { useCreateRadarSession } from '@order/integrations-stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';
import { getFetchOptions } from '@order/utils';

import { AZURE_API_ACCESS_TOKEN_HEADER } from '../../config';
import { useCreateAccountMutation } from '../../graphql/CreateAccount.generated';
import { useSignInCustomerMutation } from '../../graphql/SignInCustomer.generated';
import { checkIfAccessTokenHasExpired } from '../../helpers';
import { azureAuthMachine } from '../../machine/azure-auth-machine';
import {
  type AccessTokens,
  type AccountData,
  type AzureAuthMachineAction,
  type AzureAuthMachineGuard,
  type CreateAccountActorInput,
  type CreateAccountOutput,
  type RefreshAccessTokensInput,
  type SignInInput,
  type SignInOutput,
} from '../../machine/azure-auth-machine.types';
import { authenticate, handleRedirect, initialize, refreshAccessTokens } from '../../services';
import { useShouldPreferEphemeralSession } from '../useShouldPreferEphemeralSession';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a connected Azure auth machine service.
 */
export const useAzureAuthService = () => {
  const { push: addBanner } = useNoticeBannersStackActions();
  const { track } = useTelemetry();
  const { logCompleteRegistrationEvent } = useAppsFlyer();

  // ─── Flags ───────────────────────────────────────────────────────────

  const shouldPreferEphemeralSession = useShouldPreferEphemeralSession();
  const isRadarRegistrationEvaluationEnabled = useFeatureFlag(
    'CELS-5659-radar-registration-evaluation-enabled',
  );

  // ─── Radar ───────────────────────────────────────────────────────────

  const createRadarSession = useCreateRadarSession();

  // ─── Network Requests ────────────────────────────────────────────────

  const [, executeSignIn] = useSignInCustomerMutation();
  const [, executeCreateAccount] = useCreateAccountMutation();

  // ─── Services ────────────────────────────────────────────────────────

  /**
   * Returns a "sign in" service that attempts to sign in based on the
   * provided access tokens.
   */
  const signIn = useMemo(
    () =>
      fromPromise<SignInOutput, SignInInput>(async ({ input }) => {
        const { apiAccessToken } = input;

        if (!apiAccessToken) {
          throw new Error('Sign in failed | No access tokens');
        }

        const response = await executeSignIn(
          {},
          {
            fetchOptions: getFetchOptions({
              extraHeaders: {
                [AZURE_API_ACCESS_TOKEN_HEADER]: apiAccessToken,
              },
            }),
          },
        );
        const { data, error } = response;
        const responseType = data?.signIn.__typename;

        const hasUserError = responseType === 'ValidationError' || responseType === 'SignInFailed';
        const systemError = hasUserError ? data?.signIn.message : error?.message;

        if (responseType === 'SignInSuccess') {
          return { hasSignedIn: true };
        }

        if (responseType === 'CustomerNotFound') {
          return { hasSignedIn: false };
        }

        throw new Error(systemError);
      }),
    [executeSignIn],
  );

  /**
   * Returns a "create account" service that attempts to create an account
   * based on the provided access tokens.
   */
  const createAccount = useMemo(
    () =>
      fromPromise<CreateAccountOutput, CreateAccountActorInput>(async (args) => {
        const { email, apiAccessToken, createAccountInput: input } = args.input;

        if (!apiAccessToken || !input) {
          return { hasCreatedAccount: false };
        }

        const radarSession = isRadarRegistrationEvaluationEnabled
          ? await createRadarSession()
          : undefined;

        const response = await executeCreateAccount(
          { input: { ...input, radarSession } },
          {
            fetchOptions: getFetchOptions({
              extraHeaders: {
                [AZURE_API_ACCESS_TOKEN_HEADER]: apiAccessToken,
              },
            }),
          },
        );

        const data = response.data?.createAccount;
        const hasCreatedAccount = data?.__typename === 'CreateAccountSuccess';

        // ─── Error Handling ──────────────────────────────────────────────────

        if (!hasCreatedAccount || response.error) {
          const hasValidationErrors = data?.__typename === 'ValidationError';

          const validationError = hasValidationErrors ? data.fieldErrors?.[0]?.message : undefined;
          const userError =
            validationError ?? 'Something went wrong, try again in a few moments...';
          const systemError = response.error?.message;

          addBanner({ text: userError, palette: 'caution' });

          track('join.failure', { userError, systemError });

          return { hasCreatedAccount };
        }

        // ─────────────────────────────────────────────────────────────────────

        track('join.success', {
          name: `${input.firstName} ${input.lastName}`,
          email,
          date: format(new Date(), 'M/dd/yyyy K:mmbbb'),
          referred: false,
        });

        logCompleteRegistrationEvent();

        return { hasCreatedAccount };
      }),
    [
      addBanner,
      createRadarSession,
      executeCreateAccount,
      isRadarRegistrationEvaluationEnabled,
      logCompleteRegistrationEvent,
      track,
    ],
  );

  const handleAuthenticate = useMemo(
    () => fromPromise<AccountData>(async () => authenticate({ shouldPreferEphemeralSession })),
    [shouldPreferEphemeralSession],
  );

  // ─────────────────────────────────────────────────────────────────────

  const onSystemError = useCallback<AzureAuthMachineAction<{ systemError: unknown }>>(
    (_args, params) => {
      track('join-or-sign-in.failure', {
        systemError: params.systemError as string,
      });
    },
    [track],
  );

  return useActorRef(
    azureAuthMachine.provide({
      actions: { onSystemError },
      guards: { checkIfNeedToRefreshTokens },
      actors: {
        createAccount,
        signIn,
        authenticate: handleAuthenticate,
        initialize: initializeActor,
        handleRedirect: handleRedirectActor,
        refreshAccessTokens: refreshAccessTokensActor,
      },
    }),
  );
};

// ─── Module-Scope Implementations ────────────────────────────────────────────

const initializeActor = fromPromise<unknown>(async () => initialize());

const handleRedirectActor = fromPromise<AccountData>(async () => handleRedirect());

const refreshAccessTokensActor = fromPromise<AccessTokens, RefreshAccessTokensInput>(
  async ({ input }) => refreshAccessTokens(input.apiRefreshToken),
);

const checkIfNeedToRefreshTokens: AzureAuthMachineGuard = ({ context }) => {
  const { apiAccessToken } = context;

  if (!apiAccessToken) return true;

  return checkIfAccessTokenHasExpired({
    accessToken: apiAccessToken,
    safeGuardMinutes: 5,
  });
};
